import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Button, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import {
  useDeleteFacultyFieldsMutation,
  useFacultyFieldsListQuery,
} from "api/faculty/faculty.mutation";
import ExcelIcon from "assets/admin/excel.svg";
import { ConfirmDeleteModal } from "components/ConfirmDeleteModal";
import { Modal } from "components/Modal";
import SearchField from "components/SearchField";
import { Table } from "components/Table";
import { exportToExcel } from "constants/export";
import { handleError } from "functions/apiHandleError";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreateUpdateFieldModal } from "./components/createUpdateModal";
import classes from "./style.module.scss";

export const FacultyFieldsModal = ({ open, onClose, facultyID }) => {
  const [searchParams] = useSearchParams();
  const paramPage = Number(searchParams.get("fields_page")) || 1;

  const [pageFilter, setPageFilter] = useState({
    page: paramPage,
    page_size: 15,
    total: 1,
    count: 0,
  });
  const [searchValue, setSearchValue] = useState("");
  const [createUpdateModal, setCreateUpdateModal] = useState({
    open: false,
    selectedField: null,
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, selectedField: null });

  const facultyFieldsQuery = useFacultyFieldsListQuery({
    faculty: facultyID,
    page: pageFilter.page,
    page_size: pageFilter.page_size,
    search: searchValue,
  });
  const deleteFacultyFieldsMutation = useDeleteFacultyFieldsMutation();
  const tableData = facultyFieldsQuery.data?.results ?? [];
  const totalPages = facultyFieldsQuery.data?.total ?? pageFilter.total;

  const handleDeleteField = async () => {
    const fieldID = deleteModal.selectedField?.id;
    if (!fieldID) return;

    try {
      await deleteFacultyFieldsMutation.mutateAsync(fieldID);
      setDeleteModal({ open: false, selectedField: null });
    } catch (err) {
      handleError({ err });
    }
  };
  const handleCloseCreateUpdateModal = () => {
    setCreateUpdateModal({
      open: false,
      selectedField: null,
    });
  };

  useEffect(() => {
    if (pageFilter.page !== paramPage) {
      setPageFilter((prev) => ({
        ...prev,
        page: paramPage,
      }));
    }
  }, [paramPage]);

  const columns = [
    {
      field: "fa_name",
      headerName: "نام فارسی",
      minWidth: 130,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "en_name",
      headerName: "نام انگلیسی",
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "ar_name",
      headerName: "نام عربی",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "max_members",
      headerName: "حداکثر ثبت نامی",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "bachelor_active",
      headerName: "فعال در کارشناسی؟",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderExport: ({ row }) => (row?.bachelor_active ? "بله" : "خیر"),
      renderCell: ({ row }) => (
        <IconButton color={row.bachelor_active ? "success" : "error"}>
          {row.bachelor_active ? (
            <CheckCircleIcon fontSize="medium" />
          ) : (
            <HighlightOffIcon fontSize="medium" />
          )}
        </IconButton>
      ),
    },
    {
      field: "master_active",
      headerName: "فعال در ارشد؟",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderExport: ({ row }) => (row?.master_active ? "بله" : "خیر"),
      renderCell: ({ row }) => (
        <IconButton color={row.master_active ? "success" : "error"}>
          {row.master_active ? (
            <CheckCircleIcon fontSize="medium" />
          ) : (
            <HighlightOffIcon fontSize="medium" />
          )}
        </IconButton>
      ),
    },
    {
      field: "phd_active",
      headerName: "فعال در دکتری؟",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderExport: ({ row }) => (row?.phd_active ? "بله" : "خیر"),
      renderCell: ({ row }) => (
        <IconButton color={row.phd_active ? "success" : "error"}>
          {row.phd_active ? (
            <CheckCircleIcon fontSize="medium" />
          ) : (
            <HighlightOffIcon fontSize="medium" />
          )}
        </IconButton>
      ),
    },
    {
      field: "requires_an_interview",
      headerName: "نیاز به مصاحبه؟",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderExport: ({ row }) => (row?.requires_an_interview ? "بله" : "خیر"),
      renderCell: ({ row }) => (
        <IconButton color={row.requires_an_interview ? "success" : "error"}>
          {row.requires_an_interview ? (
            <CheckCircleIcon fontSize="medium" />
          ) : (
            <HighlightOffIcon fontSize="medium" />
          )}
        </IconButton>
      ),
    },
    {
      field: "actions",
      headerName: "عملیات",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      hideInExport: true,
      renderCell: ({ row }) => (
        <Stack spacing={2} direction="row">
          <Tooltip title="ویرایش">
            <IconButton
              color="primary"
              onClick={() =>
                setCreateUpdateModal({
                  open: true,
                  selectedField: row,
                })
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="حذف">
            <IconButton
              color="error"
              onClick={() => setDeleteModal({ open: true, selectedField: row })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Modal
        open={open}
        title=""
        onClose={onClose}
        maxWidth="lg"
        footerEnd={
          <Button variant="contained" color="error" size="xsmall" onClick={onClose}>
            بستن
          </Button>
        }
      >
        <div className={classes.mainDiv}>
          <div className={classes.toolBar}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SearchField setValueCallback={setSearchValue} variant="standard" />
              </Grid>
              <Grid item xs={0} md={4} />
              <Grid item xs={12} md={4}>
                <div className={classes.manageBar}>
                  <Tooltip title="ایجاد رشته">
                    <IconButton
                      onClick={() => setCreateUpdateModal({ open: true, selectedField: null })}
                      color="success"
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="خروجی اکسل">
                    <IconButton
                      onClick={() => exportToExcel(columns, tableData, "field_of_studies.xlsx")}
                      color="success"
                    >
                      <img src={ExcelIcon} alt="export-excell" className={classes.customIcon} />
                    </IconButton>
                  </Tooltip>
                </div>
              </Grid>
            </Grid>
          </div>
          <Table
            rows={tableData}
            columns={columns}
            totalPages={totalPages}
            loading={facultyFieldsQuery.isFetching || deleteFacultyFieldsMutation.isPending}
            className={classes.tableContainer}
            pageParamName="fields_page"
          />
        </div>
      </Modal>
      <CreateUpdateFieldModal
        open={createUpdateModal.open}
        onClose={handleCloseCreateUpdateModal}
        facultyID={facultyID}
        defaultData={createUpdateModal.selectedField}
      />
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, selectedField: null })}
        onConfirm={handleDeleteField}
        loading={deleteFacultyFieldsMutation.isPending}
        title="حذف رشته تحصیلی"
        message="آیا از حذف رشته تحصیلی انتخاب‌شده مطمئن هستید؟ این عملیات قابل بازگشت نیست."
      />
    </>
  );
};
